'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import type { GroupBase, OptionProps, SingleValueProps, StylesConfig } from 'react-select';
import type { SingleValue } from 'react-select';
import { DateInputWithFooter } from '@/app/(main)/clients/create/components/DateInputWithFooter';
import { DropZoneArea } from '@/app/components/DropZoneArea';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { ViewFileByType } from '@/app/components/ViewFileByType';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import PdfPreview from '@/app/components/cards/FileCard/PdfPreview';
import { AgentSelector } from '@/app/components/form/AgentSelector';
import { Input } from '@/app/components/form/Input';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { TextArea } from '@/app/components/form/TextArea';
import { ALLOWED_EXTENSIONS, ALLOWED_EXTENSIONS_STRING, MAX_TOTAL_SIZE } from '@/consts/const';
import { useDeleteMediaAttachmentMutation } from '@/hooks/useDeleteMediaAttachmentMutation';
import { useFileDrop } from '@/hooks/useFileDrop';
import { useUploadAndAttachMedia } from '@/hooks/useUploadAndAttachMedia';
import type { ErrorResponse } from '@/types/apiTypes';
import type { ClientTaskData, TaskStatus, TaskAttachment, UpdateClientTaskRequest } from '@/types/clientTasksApiTypes';
import { fileTypes } from '@/types/common';
import { formatMMDDYYYYToYYYYMMDD, formatYYYYMMDDToMMDDYYYY } from '@/utils/formatDate';
import { setBEFieldError } from '@/utils/setBEFieldError';
import { TASK_STATUS_CONFIG, TASK_STATUS_OPTIONS, TASK_DETAILS_MAX_LENGTH } from '../../../../../consts/client-detail';
import { getReminderDaysFromTask } from '../../utils/getReminderDaysFromTask';
import { getTaskPersonName } from '../../utils/getTaskPersonName';
import { type TaskFormValues, taskFormValidationSchema } from '../../utils/taskFormValidationSchema';
import { TaskReminderSelector } from '../TaskReminderSelector';
import ChevronDownIcon from '/public/icons/chevron-down.svg';
import CrossImageIcon from '/public/icons/cross-image.svg';
import DeleteIcon from '/public/icons/delete.svg';
import FileIcon from '/public/icons/file.svg';
import UploadIcon from '/public/icons/upload-cloud.svg';

const StatusOption = (props: OptionProps<SelectorOption<TaskStatus>, false, GroupBase<SelectorOption<TaskStatus>>>) => {
  const { innerProps, data, isSelected, isFocused } = props;
  const isActive = isSelected || isFocused;
  return (
    <div style={{ padding: '1px 6px' }}>
      <div
        {...innerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: '10px',
          paddingBottom: '10px',
          paddingLeft: '8px',
          paddingRight: '10px',
          borderRadius: '6px',
          cursor: 'pointer',
          background: isActive ? '#F2F6FC' : 'transparent',
          gap: '8px',
        }}
      >
        <span
          className={clsx(
            'flex-1 font-inter font-normal text-body-responsive',
            isActive ? 'text-black' : 'text-grey-700'
          )}
        >
          {data.label}
        </span>
        {isActive && (
          <ChevronDownIcon
            className="w-4 h-4 shrink-0 text-[#212529] fill-current"
            style={{ transform: 'rotate(-90deg)' }}
          />
        )}
      </div>
    </div>
  );
};

const StatusSingleValue = (
  props: SingleValueProps<SelectorOption<TaskStatus>, false, GroupBase<SelectorOption<TaskStatus>>>
) => {
  const { data } = props;
  const { bg } = (data.value ? TASK_STATUS_CONFIG[data.value] : null) ?? TASK_STATUS_CONFIG.OPEN;
  return (
    <div
      style={{
        height: '22px',
        paddingLeft: '8px',
        paddingRight: '8px',
        paddingTop: '2px',
        paddingBottom: '2px',
        background: bg,
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span
        className="font-inter font-normal text-body-responsive text-[#062060]"
      >
        {data.label}
      </span>
    </div>
  );
};

const statusSelectorStyles: Partial<StylesConfig<SelectorOption<TaskStatus>, false>> = {
  control: (base, state) => ({
    ...base,
    minHeight: '44px',
    height: '44px',
    padding: '0 12px',
    cursor: 'pointer',
    boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    border: state.menuIsOpen ? '1px solid var(--color-blue-300)' : '1px solid var(--color-grey-400)',
    '&:hover': { border: state.menuIsOpen ? '1px solid var(--color-blue-300)' : '1px solid var(--color-grey-400)' },
  }),
  valueContainer: (base) => ({ ...base, padding: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }),
  indicatorsContainer: (base) => ({ ...base, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 0 }),
  menuList: (base) => ({ ...base, padding: '4px 0' }),
  menu: (base) => ({
    ...base,
    boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.12)',
    border: 'none',
    borderRadius: '6px',
    minWidth: '100%',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export interface EditTaskSaveCallbacks {
  onSuccess?: () => void;
  onError?: (error: ErrorResponse<unknown>) => void;
}

interface EditTaskModalProps {
  task: ClientTaskData | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskId: number, data: UpdateClientTaskRequest, callbacks?: EditTaskSaveCallbacks) => void;
  onDelete?: () => void;
  isPending?: boolean;
  /** When true, file upload UI is hidden and new files are not uploaded (e.g. calendar mock tasks). */
  disableFileUpload?: boolean;
}

const getEditTaskFormValues = (task: ClientTaskData): TaskFormValues => ({
  title: task.title ?? '',
  deadline: formatYYYYMMDDToMMDDYYYY(task.deadline?.slice(0, 10)) ?? '',
  reminder_days: getReminderDaysFromTask(task),
  status: task.status ?? 'OPEN',
  description: task.description ?? task.details ?? '',
  agent_id: task.agent
    ? {
        value: String(task.agent.id),
        label: getTaskPersonName(task.agent),
        image: task.agent.image_relative_url ?? task.agent.profile_picture_url ?? undefined,
      }
    : null,
});

export const EditTaskModal = ({
  task,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isPending = false,
  disableFileUpload = false,
}: EditTaskModalProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<TaskAttachment[]>([]);
  const [selectedPreview, setSelectedPreview] = useState<{ url: string; name: string; isPdf: boolean } | null>(null);

  const { uploadAndAttach, isBusy: isUploading } = useUploadAndAttachMedia();
  const { mutateAsync: deleteAttachment } = useDeleteMediaAttachmentMutation();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    trigger,
    formState: { errors, isValid },
  } = useForm<TaskFormValues>({
    resolver: yupResolver(taskFormValidationSchema) as Resolver<TaskFormValues>,
    mode: 'onChange',
    defaultValues: {
      title: '',
      deadline: '',
      status: 'OPEN',
      agent_id: null,
      description: '',
      reminder_days: null,
    },
  });

  const description = watch('description') ?? '';

  useEffect(() => {
    if (task) {
      reset(getEditTaskFormValues(task));
      void trigger();
      setFiles([]);
      setExistingAttachments(task.attachments ?? []);
    }
  }, [task, reset, trigger]);

  const existingAttachmentsSize = existingAttachments.reduce((sum, a) => sum + (a.media?.file_size_bytes ?? 0), 0);
  const newFilesSize = files.reduce((sum, f) => sum + f.size, 0);
  const totalSize = existingAttachmentsSize + newFilesSize;
  const isTotalSizeExceeded = totalSize > MAX_TOTAL_SIZE;

  const { isDragging, inputRefProps, dropZoneProps, isAllowedExtensionError } = useFileDrop({
    setFiles: (newFiles) => setFiles(newFiles),
    files,
    allowedExtensions: ALLOWED_EXTENSIONS,
    isMultipleFiles: true,
    maxFileSize: MAX_TOTAL_SIZE, // per-file limit same as total; total check is done separately
  });

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  const handleDeleteExistingAttachment = async (uuid: string) => {
    try {
      await deleteAttachment(uuid);
      setExistingAttachments((prev) => prev.filter((a) => a.uuid !== uuid));
    } catch {
      // deletion error is handled by the mutation
    }
  };

  const onSubmit = async (values: TaskFormValues) => {
    if (!task || !values.title.trim()) return;

    clearErrors();

    if (!disableFileUpload && files.length > 0) {
      try {
        await Promise.all(
          files.map((file, index) =>
            uploadAndAttach(
              file,
              {
                attachable_type: 'TASK',
                attachable_id: task.id,
                attachment_role: 'TASK_ATTACHMENT',
                sort_order: existingAttachments.length + index,
                is_primary: existingAttachments.length === 0 && index === 0,
              },
              {
                uploadContext: 'TASKS',
                visibility: 'SENSITIVE',
              }
            )
          )
        );
        setFiles([]);
      } catch {
        // upload error handled in hook
      }
    }

    onSave(
      task.id,
      {
        title: values.title.trim(),
        description: values.description.trim() || null,
        agent_id: values.agent_id?.value ? Number(values.agent_id.value) : null,
        status: values.status,
        deadline: formatMMDDYYYYToYYYYMMDD(values.deadline) ?? undefined,
        reminder_days: values.reminder_days,
      },
      {
        onError: (error) => {
          setBEFieldError(error, setError);
        },
      }
    );
  };

  const isSaving = isPending || isUploading;

  if (!task) return null;

  return (
    <>
      {selectedPreview && (
        <Modal open={!!selectedPreview} onClose={() => setSelectedPreview(null)} variant="fullScreenMobile">
          <div className="flex flex-col items-center gap-6 overflow-hidden max-md:h-full max-md:w-full max-md:px-4 max-md:py-8 md:py-[46px] md:px-9 md:w-[550px]">
            <h2 className="font-canela font-light text-[28px] leading-[36.4px] tracking-[0.6px] text-center text-blue-900">
              {selectedPreview.name}
            </h2>
            <ViewFileByType
              type={selectedPreview.isPdf ? fileTypes.PDF : fileTypes.IMAGE}
              path={selectedPreview.url}
              caption={selectedPreview.name}
              name={selectedPreview.name}
              contentDimensions={{ width: 472, height: 420 }}
            />
            <BaseButton
              intent="secondary"
              className="w-full !px-[22px] !py-[9px] !text-base !font-medium !leading-6 !h-auto !min-h-0 !rounded-[6px]"
              onClick={() => setSelectedPreview(null)}
            >
              Close
            </BaseButton>
          </div>
        </Modal>
      )}
      <Modal open={isOpen} onClose={handleClose} variant="fullScreenMobile">
        <div className="flex flex-col overflow-hidden max-md:h-full max-md:w-full md:w-[550px] md:max-h-[90vh]">
          <div className="flex flex-col gap-[6px] px-4 pt-8 md:px-9 md:pt-[46px] pb-6 text-center shrink-0 max-md:pb-4">
            <ModalMobileHeader title="Edit task" onBack={handleClose} />
            <p className="font-inter font-normal text-[16px] leading-6 text-grey-700 max-md:hidden">
              Edit the details below as needed.
            </p>
          </div>

          <form
            id="edit-task-form"
            className="flex flex-col gap-5 px-4 md:px-9 overflow-y-auto flex-1 min-h-0"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <Input
              {...register('title')}
              name="title"
              label="Task title"
              required
              placeholder="Enter task title here"
              errors={errors}
            />

            <div className="flex gap-3">
              <div className="flex-1">
                <Controller
                  name="deadline"
                  control={control}
                  render={({ field }) => (
                    <DateInputWithFooter
                      label="Deadline"
                      name="deadline"
                      required
                      placeholder="MM/DD/YYYY"
                      value={field.value}
                      onChangeDate={field.onChange}
                      minDate={new Date()}
                      dateFormat="MM/dd/yyyy"
                      errors={errors}
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Selector<TaskStatus>
                      label="Status"
                      name="status"
                      options={TASK_STATUS_OPTIONS}
                      value={field.value}
                      errors={errors}
                      onChange={(opt: SingleValue<SelectorOption<TaskStatus>>) => {
                        if (opt?.value) field.onChange(opt.value);
                      }}
                      isClearOption={false}
                      isSearchable={false}
                      customStyles={statusSelectorStyles}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        Option: StatusOption as never,
                        SingleValue: StatusSingleValue as never,
                        DropdownIndicator: (props) => (
                          <ChevronDownIcon
                            className={`transition-transform duration-200 text-black fill-current w-4 h-4 ${props.selectProps.menuIsOpen ? 'rotate-180' : ''}`}
                          />
                        ),
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <Controller
                  name="agent_id"
                  control={control}
                  render={({ field }) => (
                    <AgentSelector
                      label="Task assignee"
                      name="agent_id"
                      placeholder="Select team member"
                      value={field.value}
                      errors={errors}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <Controller
                  name="reminder_days"
                  control={control}
                  render={({ field }) => (
                    <TaskReminderSelector value={field.value} errors={errors} onChange={field.onChange} />
                  )}
                />
              </div>
            </div>

            <TextArea
              {...register('description')}
              name="description"
              label="Task details"
              placeholder="Add context, notes, or specific instructions"
              errors={errors}
              maxLength={TASK_DETAILS_MAX_LENGTH}
              value={description}
              rows={4}
            />

            {/* File upload */}
            {!disableFileUpload && (
              <>
                <input
                  accept={ALLOWED_EXTENSIONS_STRING}
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  {...inputRefProps}
                />
                <DropZoneArea
                  {...dropZoneProps}
                  uploadIcon={FileIcon}
                  className={clsx('px-[50px] py-[36px]', {
                    'bg-red-50': isAllowedExtensionError || isTotalSizeExceeded,
                  })}
                  isDragging={isDragging}
                  isError={isAllowedExtensionError || isTotalSizeExceeded}
                >
                  <div className="flex flex-col gap-[10px] items-center justify-center">
                    <p className="text-button-medium text-black mt-5 text-center">Drag your file(s) to start uploading</p>
                    <div className="flex flex-row gap-[12px] items-center justify-center">
                      <span className="w-[82px] h-[1px] bg-grey-400 block" />
                      <span className="text-body-small text-neutral-500">or</span>
                      <span className="w-[82px] h-[1px] bg-grey-400 block" />
                    </div>
                    <BaseButton
                      type="button"
                      startIcon={<UploadIcon className="w-4 h-4 text-blue-300 fill-current" />}
                      intent="ghost"
                      size="medium"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload Files
                    </BaseButton>
                  </div>
                  <p className="text-body-small text-neutral-500 mt-[18px] text-center">
                    PNG, JPG/JPEG, HEIC, PDF · Max 10 files · Max 50 MB total
                  </p>
                  {isAllowedExtensionError && (
                    <p className="text-center text-red-500 text-tp-body-small mt-2">File type(s) not supported</p>
                  )}
                  {isTotalSizeExceeded && (
                    <p className="text-center text-red-500 text-tp-body-small mt-2">Total size exceeds 50 MB limit</p>
                  )}
                </DropZoneArea>
              </>
            )}

            {/* Attachments: existing + pending new files */}
            {!disableFileUpload && (existingAttachments.length > 0 || files.length > 0) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, 85px)',
                  gap: '6px',
                  justifyContent: 'space-between',
                }}
              >
                {existingAttachments.map((attachment) => {
                  const isPdf =
                    attachment.media?.file_mime_type === 'application/pdf' ||
                    attachment.media?.remote_url?.toLowerCase().endsWith('.pdf');
                  const url = attachment.media?.remote_url ?? '';
                  const name = attachment.media?.raw_filename ?? 'attachment';
                  return (
                    <div key={attachment.uuid} className="relative w-[88px] h-[88px]">
                      <button
                        type="button"
                        onClick={() => setSelectedPreview({ url, name, isPdf })}
                        className="w-[82px] h-[82px] rounded-[6px] overflow-hidden border border-blue-100 cursor-pointer hover:opacity-80 transition-opacity block"
                      >
                        {isPdf ? (
                          <PdfPreview fileUrl={url} containerWidth={82} containerHeight={82} />
                        ) : (
                          <Image
                            src={url}
                            alt={name}
                            width={82}
                            height={82}
                            unoptimized
                            className="w-full h-full object-cover"
                          />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingAttachment(attachment.uuid)}
                        className="absolute top-0 right-0 w-[22px] h-[22px] rounded-full bg-red-500 flex items-center justify-center z-10 cursor-pointer"
                      >
                        <CrossImageIcon className="w-[10px] h-[10px] text-white fill-current" />
                      </button>
                    </div>
                  );
                })}
                {files.map((file, idx) => {
                  const fileUrl = URL.createObjectURL(file);
                  const isFilePdf = file.type === 'application/pdf';
                  const isImage = file.type.startsWith('image/');
                  return (
                    <div key={`new-${idx}`} className="relative w-[88px] h-[88px]">
                      <button
                        type="button"
                        onClick={() => setSelectedPreview({ url: fileUrl, name: file.name, isPdf: isFilePdf })}
                        className="w-[82px] h-[82px] rounded-[6px] overflow-hidden border border-blue-100 cursor-pointer hover:opacity-80 transition-opacity block"
                      >
                        {isFilePdf ? (
                          <PdfPreview fileUrl={fileUrl} containerWidth={82} containerHeight={82} />
                        ) : isImage ? (
                          <Image
                            src={fileUrl}
                            alt={file.name}
                            width={82}
                            height={82}
                            unoptimized
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-grey-100 flex flex-col items-center justify-center gap-1 px-1">
                            <FileIcon className="w-5 h-5 text-neutral-500" />
                            <span className="text-[8px] text-neutral-500 text-center truncate w-full px-1">
                              {file.name}
                            </span>
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                        className="absolute top-0 right-0 w-[22px] h-[22px] rounded-full bg-red-500 flex items-center justify-center z-10 cursor-pointer"
                      >
                        <CrossImageIcon className="w-[10px] h-[10px] text-white fill-current" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </form>

          <div className="flex flex-col gap-3 px-4 md:px-9 pt-6 pb-8 md:pb-[46px] shrink-0">
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="flex items-center gap-2 text-red-500 font-inter text-[16px] font-normal leading-6 cursor-pointer bg-transparent border-none p-0 w-fit"
              >
                <DeleteIcon className="w-4 h-4 text-red-500" />
                Delete Task
              </button>
            )}
            <div className="flex gap-5">
              <BaseButton
                intent="secondary"
                className="flex-1 !px-[22px] !py-[9px] !text-base !font-medium !leading-6 !h-auto !min-h-0 !rounded-[6px]"
                onClick={handleClose}
              >
                Cancel
              </BaseButton>
              <BaseButton
                intent="primary"
                type="submit"
                form="edit-task-form"
                className="flex-1 !px-[22px] !py-[9px] !text-base !font-medium !leading-6 !h-auto !min-h-0 !rounded-[6px]"
                disabled={!isValid || isSaving || isTotalSizeExceeded}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </BaseButton>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};